import { motion } from "framer-motion";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../components/ui/accordion";
import useApiData from "../../hooks/useApiData";

const fallbackFaqPage = {
  hero: {
    badge: "Support",
    title: "Frequently Asked Questions",
    description:
      "Find answers to common questions about DVX programs and memberships",
  },
  cta: {
    title: "Still Have Questions?",
    description:
      "Can't find what you're looking for? Our support team is here to help.",
    button_text: "Contact Support",
    button_href: "/contact",

    // NEW CTA DATA
    callNow: "+911234567890",
    bookLink: "/contact",
  },
  faqs: [],
};

export default function FAQ() {
  const {
    data: pageData,
    loading,
    error,
  } = useApiData("/faqs", fallbackFaqPage);

  const faqs = pageData?.faqs || [];

  return (
    <div
      id="faq"
      className="min-h-screen bg-[var(--dvx-bg)] pt-2 transition-colors duration-300 scroll-mt-24"
    >
      {/* HERO */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <h3 className="text-[var(--dvx-primary)] font-oswald text-xl font-semibold uppercase tracking-[0.20.em]">
              {pageData?.hero?.badge || "FAQ"}
            </h3>

            <h1 className="mt-4 font-oswald text-4xl font-bold text-[var(--dvx-text)] uppercase tracking-tight">
              {pageData?.hero?.title || "Questions & Answers"}
            </h1>

            <p className="mt-4 text-[var(--dvx-text-muted)] font-manrope text-lg max-w-2xl mx-auto">
              {pageData?.hero?.description ||
                "Find answers to common questions."}
            </p>
          </motion.div>
        </div>
      </section>

      {/* FAQ LIST */}
      <section className="pb-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* LOADING */}
          {loading && (
            <div className="flex justify-center py-20">
              <div className="font-oswald text-xl text-[var(--dvx-text)] uppercase tracking-widest animate-pulse">
                Loading FAQs...
              </div>
            </div>
          )}

          {/* ERROR */}
          {!loading && error && (
            <div className="text-center py-20">
              <p className="text-red-500 font-oswald uppercase">
                Failed to load FAQs.
              </p>
              <p className="text-sm text-[var(--dvx-text-muted)] mt-2">
                Please try again later.
              </p>
            </div>
          )}

          {/* EMPTY */}
          {!loading && !error && faqs.length === 0 && (
            <div className="text-center py-20">
              <h3 className="font-oswald text-2xl text-[var(--dvx-text)] uppercase">
                No FAQs Found
              </h3>
              <p className="text-[var(--dvx-text-muted)] mt-2">
                We currently don’t have any questions available.
              </p>
            </div>
          )}

          {/* ACCORDION */}
          {!loading && !error && faqs.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Accordion type="single" collapsible className="space-y-4">
                {faqs.map((faq, index) => (
                  <AccordionItem
                    key={`${faq?.question || index}-${index}`}
                    value={`item-${index}`}
                    className="border border-[var(--dvx-border)] bg-[var(--dvx-surface)] px-6 rounded-2xl shadow-sm"
                  >
                    <AccordionTrigger className="font-oswald text-[var(--dvx-text)] text-left uppercase tracking-wide hover:text-[var(--dvx-primary)] py-6 hover:no-underline">
                      {faq?.question || "No question provided"}
                    </AccordionTrigger>

                    <AccordionContent className="text-[var(--dvx-text-muted)] font-manrope pb-6 leading-relaxed">
                      {faq?.answer || "No answer available."}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </motion.div>
          )}
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="pb-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="border border-[var(--dvx-border)] bg-[var(--dvx-surface)] p-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-6"
          >
            {/* TEXT */}
            <div>
              <h3 className="font-oswald text-lg font-bold uppercase text-[var(--dvx-text)] mb-2">
                Still need help?
              </h3>
              <p className="text-sm text-[var(--dvx-text-muted)] max-w-xl">
                Get direct guidance for your goals, timeline, and current
                challenges. Our team will map the right approach for you.
              </p>
            </div>

            {/* BUTTONS */}
            <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
              {pageData?.cta?.callNow && (
                <a
                  href={`tel:${pageData.cta.callNow}`}
                  className="text-center px-5 py-2 border border-[var(--dvx-border)] text-sm font-semibold hover:bg-[var(--dvx-primary)] hover:text-white transition"
                >
                  Call Now
                </a>
              )}

              <a
                href={pageData?.cta?.bookLink || "/contact"}
                className="text-center px-5 py-2 bg-[var(--dvx-primary)] text-white text-sm font-semibold hover:opacity-90 transition"
              >
                Book Assessment
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
